// frontend/src/components/common/MobileCard.js
//
// Reusable mobile record card for the Manager Portal.
// Standard pattern (chosen 2026-06): a compact, tappable row that expands to
// reveal full detail — lets list-heavy pages fit far more per screen on mobile.
//
// Use ONLY on mobile (gate with useIsMobile); desktop keeps its existing layout.
import React, { useState } from 'react';
import { C } from '../../styles/theme';

const FONT = "'Plus Jakarta Sans', sans-serif";

const TONE = {
    red:   { bg: C.redLight, color: C.red },
    green: { bg: C.greenLight, color: C.green },
    gray:  { bg: '#f1f5f9', color: C.muted },
    amber: { bg: '#fffbeb', color: '#d97706' },
};

/**
 * @param {object}   props
 * @param {'red'|'green'|'amber'} [props.accent]   left border accent
 * @param {{initials:string, tone?:string}} [props.avatar]
 * @param {string}   props.title                    primary label (e.g. name)
 * @param {string}   [props.subtitle]               secondary line (id · meta)
 * @param {{label?:string, icon?:React.node, tone?:string}} [props.status]  pill in the row
 * @param {string|number} [props.amount]            trailing bold value
 * @param {boolean}  [props.collapsible=true]       false = always-open (no chevron)
 * @param {boolean}  [props.defaultOpen=false]
 * @param {React.node} [props.children]             expandable detail content
 */
export default function MobileCard({
    accent, avatar, title, subtitle, status, amount,
    collapsible = true, defaultOpen = false, children,
}) {
    const [open, setOpen] = useState(defaultOpen);
    const isOpen = collapsible ? open : true;
    const accentColor = accent === 'red' ? C.red : accent === 'green' ? C.green : accent === 'amber' ? '#d97706' : null;
    const av = avatar ? (TONE[avatar.tone] || TONE.gray) : null;
    const st = status ? (TONE[status.tone] || TONE.gray) : null;

    return (
        <div style={{
            background: C.white, border: `1px solid ${C.border}`, borderRadius: 14, marginBottom: 12,
            borderLeft: accentColor ? `4px solid ${accentColor}` : `1px solid ${C.border}`,
            overflow: 'hidden', fontFamily: FONT,
        }}>
            <div
                onClick={collapsible ? () => setOpen(o => !o) : undefined}
                role={collapsible ? 'button' : undefined}
                aria-expanded={collapsible ? isOpen : undefined}
                style={{
                    display: 'flex', alignItems: 'center', gap: 12, padding: 14,
                    cursor: collapsible ? 'pointer' : 'default', minHeight: 44,
                }}
            >
                {avatar && (
                    <div style={{ position: 'relative', flexShrink: 0 }}>
                        <div style={{
                            width: 38, height: 38, borderRadius: 10,
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            fontWeight: 800, fontSize: 14, background: av.bg, color: av.color,
                        }}>{avatar.initials}</div>
                        {avatar.ping && (
                            <span style={{
                                position: 'absolute', top: -3, right: -3, width: 12, height: 12, borderRadius: '50%',
                                border: '2.5px solid #fff', background: avatar.ping === 'green' ? C.green : avatar.ping === 'amber' ? '#d97706' : C.red,
                            }} />
                        )}
                    </div>
                )}
                <div style={{ flex: 1, minWidth: 0 }}>
                    {/* Line 1: name owns the row; amount + chevron sit to the right */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span style={{ flex: 1, minWidth: 0, fontSize: 15, fontWeight: 700, color: C.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{title}</span>
                        {amount != null && amount !== '' && (
                            <span style={{ fontSize: 15, fontWeight: 800, color: C.text, whiteSpace: 'nowrap', flexShrink: 0 }}>{amount}</span>
                        )}
                        {collapsible && (
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={C.mutedLight} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, transition: 'transform 0.2s', transform: isOpen ? 'rotate(90deg)' : 'none' }}>
                                <polyline points="9 18 15 12 9 6" />
                            </svg>
                        )}
                    </div>
                    {/* Line 2: status (pill or plain colored text) + meta */}
                    {(status || subtitle) && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 4, minWidth: 0 }}>
                            {status && (status.plain ? (
                                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3, flexShrink: 0, fontSize: 12, fontWeight: 700, color: st.color }}>{status.icon}{status.label}</span>
                            ) : (
                                <span style={{
                                    display: 'inline-flex', alignItems: 'center', gap: 3, flexShrink: 0,
                                    fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 20,
                                    background: st.bg, color: st.color,
                                }}>{status.icon}{status.label}</span>
                            ))}
                            {subtitle && <span style={{ flex: 1, minWidth: 0, fontSize: 12, color: C.muted, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', textAlign: status && status.plain ? 'right' : 'left' }}>{subtitle}</span>}
                        </div>
                    )}
                </div>
            </div>
            {isOpen && children != null && (
                <div className="mobilecard-detail" style={{ padding: '14px', borderTop: '1px solid #f1f5f9' }}>
                    {children}
                </div>
            )}
        </div>
    );
}
